"use client";

import * as React from "react";
import type { Editor } from "@tiptap/react";
import {
  AtSign,
  Code2,
  Heading2,
  Heading3,
  Link as LinkIcon,
  List,
  ListOrdered,
  Minus,
  Pilcrow,
  Quote,
  Smile,
} from "lucide-react";

import EmojiPicker from "@/components/threads/emoji-picker";

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/**
 * A discriminated action emitted when the user picks an item from the menu.
 * Decoupled from any Editor instance so the menu can be used in places that
 * don't have a TipTap editor mounted yet (e.g. compact composer that defers
 * the action until the expanded modal opens).
 */
export type BlockInsertAction =
  | { type: "command"; commandId: BlockCommandId }
  | { type: "insertText"; text: string }
  | { type: "insertLink"; href: string; text: string };

export type BlockCommandId =
  | "paragraph"
  | "heading-2"
  | "heading-3"
  | "ordered-list"
  | "bullet-list"
  | "blockquote"
  | "divider"
  | "code-block"
  | "mention";

// ---------------------------------------------------------------------------
// Action → editor command resolver
// ---------------------------------------------------------------------------

/**
 * Apply a {@link BlockInsertAction} to a TipTap editor instance.
 * Use this in the call site after receiving the action from `onSelect` (or
 * after the editor becomes ready in deferred-action flows).
 */
export function applyBlockInsertAction(
  action: BlockInsertAction,
  editor: Editor,
): void {
  if (action.type === "insertText") {
    editor.chain().focus().insertContent(action.text).run();
    return;
  }
  if (action.type === "insertLink") {
    editor
      .chain()
      .focus()
      .insertContent({
        type: "text",
        text: action.text,
        marks: [{ type: "link", attrs: { href: action.href } }],
      })
      .run();
    return;
  }
  // Command
  switch (action.commandId) {
    case "paragraph":
      editor.chain().focus().setParagraph().run();
      return;
    case "heading-2":
      editor.chain().focus().toggleHeading({ level: 2 }).run();
      return;
    case "heading-3":
      editor.chain().focus().toggleHeading({ level: 3 }).run();
      return;
    case "ordered-list":
      editor.chain().focus().toggleOrderedList().run();
      return;
    case "bullet-list":
      editor.chain().focus().toggleBulletList().run();
      return;
    case "blockquote":
      editor.chain().focus().toggleBlockquote().run();
      return;
    case "divider":
      editor.chain().focus().setHorizontalRule().run();
      return;
    case "code-block":
      editor.chain().focus().toggleCodeBlock().run();
      return;
    case "mention":
      editor.chain().focus().insertContent("@").run();
      return;
  }
}

// ---------------------------------------------------------------------------
// Internal item descriptors
// ---------------------------------------------------------------------------

type ItemKind = "command" | "popover";

interface BaseItem {
  id: string;
  label: string;
  Icon: React.ComponentType<{ className?: string }>;
  kind: ItemKind;
}

interface CommandDescriptor extends BaseItem {
  kind: "command";
  commandId: BlockCommandId;
}

interface PopoverDescriptor extends BaseItem {
  kind: "popover";
  /**
   * Sub-popover content. Receives an `emit` callback to forward the resolved
   * action to the menu's `onSelect` and a `close` callback to dismiss the
   * sub-popover (and the parent menu).
   */
  renderPopover: (
    emit: (action: BlockInsertAction) => void,
    close: () => void,
  ) => React.ReactNode;
}

export type ItemDescriptor = CommandDescriptor | PopoverDescriptor;

// ---------------------------------------------------------------------------
// Sub-popover content components
// ---------------------------------------------------------------------------

interface LinkPopoverProps {
  onSubmit: (action: BlockInsertAction) => void;
}

const LinkPopover = React.memo(function LinkPopover({
  onSubmit,
}: LinkPopoverProps) {
  const [url, setUrl] = React.useState("");
  const [text, setText] = React.useState("");

  const handleSubmit = React.useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const trimmedUrl = url.trim();
      if (!trimmedUrl) return;

      const trimmedText = text.trim() || trimmedUrl;
      onSubmit({
        type: "insertLink",
        href: trimmedUrl,
        text: trimmedText,
      });
    },
    [onSubmit, url, text],
  );

  return (
    <form onSubmit={handleSubmit} className="flex w-72 flex-col gap-2 p-3">
      <label className="text-xs font-semibold text-foreground">URL</label>
      <input
        type="url"
        autoFocus
        required
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        placeholder="https://example.com"
        className="rounded-md bg-muted px-2.5 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
      />
      <label className="mt-1 text-xs font-semibold text-foreground">
        Text (optional)
      </label>
      <input
        type="text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Click here"
        className="rounded-md bg-muted px-2.5 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
      />
      <button
        type="submit"
        className="mt-2 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
      >
        Insert link
      </button>
    </form>
  );
});

// ---------------------------------------------------------------------------
// Items
// ---------------------------------------------------------------------------

export const ITEMS: ItemDescriptor[] = [
  {
    id: "paragraph",
    label: "Paragraph",
    Icon: Pilcrow,
    kind: "command",
    commandId: "paragraph",
  },
  {
    id: "heading-2",
    label: "Heading 2",
    Icon: Heading2,
    kind: "command",
    commandId: "heading-2",
  },
  {
    id: "heading-3",
    label: "Heading 3",
    Icon: Heading3,
    kind: "command",
    commandId: "heading-3",
  },
  {
    id: "ordered-list",
    label: "Numbered list",
    Icon: ListOrdered,
    kind: "command",
    commandId: "ordered-list",
  },
  {
    id: "bullet-list",
    label: "Bulleted list",
    Icon: List,
    kind: "command",
    commandId: "bullet-list",
  },
  {
    id: "blockquote",
    label: "Blockquote",
    Icon: Quote,
    kind: "command",
    commandId: "blockquote",
  },
  {
    id: "divider",
    label: "Divider",
    Icon: Minus,
    kind: "command",
    commandId: "divider",
  },
  {
    id: "code-block",
    label: "Code",
    Icon: Code2,
    kind: "command",
    commandId: "code-block",
  },
  {
    id: "emoji",
    label: "Emoji",
    Icon: Smile,
    kind: "popover",
    renderPopover: (emit) => (
      <EmojiPicker
        onEmojiSelect={(emoji) => emit({ type: "insertText", text: emoji })}
      />
    ),
  },
  {
    id: "mention",
    label: "Mention",
    Icon: AtSign,
    kind: "command",
    commandId: "mention",
  },
  {
    id: "link",
    label: "Button",
    Icon: LinkIcon,
    kind: "popover",
    renderPopover: (emit) => <LinkPopover onSubmit={emit} />,
  },
];

// ---------------------------------------------------------------------------
// Item row
// ---------------------------------------------------------------------------

interface ItemRowProps {
  item: ItemDescriptor;
  onSelect: (action: BlockInsertAction) => void;
}

const ItemRow = React.memo(function ItemRow({ item, onSelect }: ItemRowProps) {
  const [subOpen, setSubOpen] = React.useState(false);
  const subRef = React.useRef<HTMLDivElement>(null);

  const handleSubEmit = React.useCallback(
    (action: BlockInsertAction) => {
      setSubOpen(false);
      onSelect(action);
    },
    [onSelect],
  );

  const handleSubClose = React.useCallback(() => setSubOpen(false), []);

  // Close sub-menu on outside click / Escape — same non-portal approach
  // as the main menu so it works inside a Radix Dialog.
  React.useEffect(() => {
    if (!subOpen) return;
    const onMouseDown = (e: MouseEvent) => {
      if (subRef.current && !subRef.current.contains(e.target as Node)) {
        setSubOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setSubOpen(false);
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [subOpen]);

  if (item.kind === "command") {
    const handleClick = () => {
      onSelect({ type: "command", commandId: item.commandId });
    };
    return (
      <button
        type="button"
        onClick={handleClick}
        className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm text-foreground transition-colors hover:bg-accent"
      >
        <item.Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
        <span>{item.label}</span>
      </button>
    );
  }

  return (
    <div ref={subRef} className="relative">
      <button
        type="button"
        onClick={() => setSubOpen((prev) => !prev)}
        className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm text-foreground transition-colors hover:bg-accent"
      >
        <item.Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
        <span>{item.label}</span>
      </button>
      {subOpen && (
        // Opens upward from the row, inside the menu's own column width.
        // Not left-full (opens ~300px sideways) or top-full (opens downward)
        // — both got clipped by the create-post modal's `overflow-hidden`
        // DialogContent, since z-index can't rescue content outside an
        // ancestor's overflow clip box. "Button" is the last row (bottom of
        // this upward-opening menu, right above the footer), so `bottom-full`
        // uses the same headroom the menu itself already opens into
        // (TRA-1710).
        <div className="absolute bottom-full left-full z-50 mb-1 rounded-md border border-border bg-popover text-popover-foreground shadow-lg">
          {item.renderPopover(handleSubEmit, handleSubClose)}
        </div>
      )}
    </div>
  );
});

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface BlockInsertMenuProps {
  /**
   * Called with the resolved action when the user picks an item. The caller
   * decides what to do with it — apply it directly via
   * {@link applyBlockInsertAction} when an editor is available, or store it
   * and apply later (e.g. after expanding from a compact composer to a
   * modal that mounts its own editor).
   */
  onSelect: (action: BlockInsertAction) => void;
  /** The trigger element (usually the "+" button). */
  children: React.ReactNode;
  /**
   * Vertical direction the menu opens, relative to the trigger. `'top'`
   * (default) opens upward — correct for a bottom toolbar. `'bottom'` opens
   * downward — use when the trigger sits near the top of a scrollable
   * container, so the menu isn't clipped by overflow or hidden behind the
   * header above it.
   */
  side?: "top" | "bottom";
  /**
   * Horizontal edge the menu aligns to. `'start'` (default) left-aligns the
   * menu with the trigger; `'end'` right-aligns it (extends leftward) — use
   * for a right-aligned trigger so the menu doesn't overflow off the side.
   */
  align?: "start" | "end";
}

/**
 * Custom inline popover (no React Portal) so the menu works inside Radix
 * Dialog on React 19 — Radix Popover would inherit `pointer-events: none`
 * from the body via DismissableLayer and also render behind the dialog due
 * to z-index ordering. See SimpleSelect for the same workaround.
 */
export function BlockInsertMenu({
  onSelect,
  children,
  side = "top",
  align = "start",
}: BlockInsertMenuProps): React.JSX.Element {
  const [open, setOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const handleSelect = React.useCallback(
    (action: BlockInsertAction) => {
      setOpen(false);
      onSelect(action);
    },
    [onSelect],
  );

  const toggle = React.useCallback(() => setOpen((prev) => !prev), []);

  // Close on outside mousedown
  React.useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Close on Escape
  React.useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open]);

  return (
    <div ref={containerRef} className="relative inline-block">
      <div onClick={toggle}>{children}</div>
      {open && (
        <div
          role="menu"
          className={`absolute z-50 w-64 rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-lg ${
            side === "bottom" ? "top-full mt-2" : "bottom-full mb-2"
          } ${align === "end" ? "right-0" : "left-0"}`}
        >
          <div className="flex flex-col">
            {ITEMS.map((item) => (
              <ItemRow key={item.id} item={item} onSelect={handleSelect} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
